import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  MapPin, 
  User, 
  Phone, 
  Mail, 
  Calendar,
  DollarSign,
  Clock,
  Car,
  Plus,
  X,
  Loader2,
  Info,
  Check
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ConciergeRideEntry = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [priceLoading, setPriceLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    date: '',
    client_name: '',
    client_phone: '',
    client_email: '',
    pickup_location: '',
    dropoff_location: '',
    stops: [],
    miles_driven: 0,
    time_hours: 0,
    earnings: 0,
    tips: 0,
    notes: ''
  });

  const [priceBreakdown, setPriceBreakdown] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const addStop = () => {
    setFormData(prev => ({
      ...prev,
      stops: [...prev.stops, { address: '', order: prev.stops.length }]
    }));
  };

  const removeStop = (index) => {
    setFormData(prev => ({
      ...prev,
      stops: prev.stops.filter((_, i) => i !== index)
    }));
  };

  const updateStop = (index, address) => {
    setFormData(prev => ({
      ...prev,
      stops: prev.stops.map((stop, i) => 
        i === index ? { ...stop, address } : stop
      )
    }));
  };

  const calculatePrice = async () => {
    if (!formData.pickup_location || !formData.dropoff_location) {
      toast.error('Please enter pickup and dropoff locations');
      return;
    }

    setPriceLoading(true);
    try {
      const response = await axios.post(`${API}/calculate-price`, {
        pickup_location: formData.pickup_location,
        dropoff_location: formData.dropoff_location,
        stops: formData.stops.map(s => s.address).filter(Boolean)
      });
      
      const data = response.data;
      setPriceBreakdown(data);
      setFormData(prev => ({
        ...prev,
        miles_driven: data.miles,
        time_hours: data.hours,
        earnings: data.base_price
      }));
      
      toast.success('Price calculated!');
    } catch (error) {
      toast.error('Failed to calculate price: ' + (error.response?.data?.detail || error.message));
    } finally {
      setPriceLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.client_name || !formData.pickup_location || !formData.dropoff_location || !formData.date) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        date: new Date(formData.date).toISOString(),
        stops: formData.stops.filter(s => s.address),
        available_for_claim: true  // Concierge rides go to the board
      };

      await axios.post(`${API}/rides`, payload);
      toast.success('Ride posted successfully! Drivers have been notified.');
      navigate('/concierge/dashboard');
    } catch (error) {
      toast.error('Failed to post ride: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const totalAmount = (formData.earnings || 0) + (formData.tips || 0);
  const conciergeCommission = totalAmount * 0.20;
  const driverEarnings = totalAmount * 0.80;

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="concierge-ride-entry">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Post New Ride</h1>
        <p className="text-gray-500 mt-1">Create a ride for drivers to claim</p>
      </div>

      {/* Commission Info */}
      <Card className="border-purple-200 bg-purple-50">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-purple-800">
                When a driver completes this ride, you'll earn <strong>20% commission</strong>. 
                All active drivers will be notified about this new ride opportunity.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Client Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="client_name">Client Name *</Label>
              <Input
                id="client_name"
                name="client_name"
                value={formData.client_name}
                onChange={handleInputChange}
                placeholder="John Smith"
                required
                data-testid="client-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="client_phone">Phone</Label>
                <Input
                  id="client_phone"
                  name="client_phone"
                  value={formData.client_phone}
                  onChange={handleInputChange}
                  placeholder="(305) 555-0123"
                  data-testid="client-phone-input"
                />
              </div>
              <div>
                <Label htmlFor="client_email">Email</Label>
                <Input
                  id="client_email"
                  name="client_email"
                  type="email"
                  value={formData.client_email}
                  onChange={handleInputChange}
                  placeholder="client@email.com"
                  data-testid="client-email-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mt-4">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Date & Time
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div>
              <Label htmlFor="date">Pickup Date & Time *</Label>
              <Input
                id="date"
                name="date"
                type="datetime-local"
                value={formData.date}
                onChange={handleInputChange}
                required
                data-testid="date-input"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="mt-4">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Route Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="pickup_location">Pickup Location *</Label>
              <Input
                id="pickup_location"
                name="pickup_location"
                value={formData.pickup_location}
                onChange={handleInputChange}
                placeholder="123 Main St, Miami, FL"
                required
                data-testid="pickup-input"
              />
            </div>

            {/* Intermediate Stops */}
            {formData.stops.length > 0 && (
              <div className="space-y-2">
                <Label>Intermediate Stops</Label>
                {formData.stops.map((stop, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={stop.address}
                      onChange={(e) => updateStop(index, e.target.value)}
                      placeholder={`Stop ${index + 1}`}
                      data-testid={`stop-${index}-input`}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeStop(index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <Button type="button" variant="outline" onClick={addStop}>
              <Plus className="h-4 w-4 mr-2" />
              Add Stop
            </Button>

            <div>
              <Label htmlFor="dropoff_location">Dropoff Location *</Label>
              <Input
                id="dropoff_location"
                name="dropoff_location"
                value={formData.dropoff_location}
                onChange={handleInputChange}
                placeholder="Miami International Airport"
                required
                data-testid="dropoff-input"
              />
            </div>

            <Button 
              type="button" 
              variant="secondary" 
              onClick={calculatePrice}
              disabled={priceLoading}
              className="w-full"
            >
              {priceLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <DollarSign className="h-4 w-4 mr-2" />
              )}
              Calculate Price
            </Button>
          </CardContent>
        </Card>

        <Card className="mt-4">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Pricing
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {priceBreakdown && (
              <div className="bg-gray-50 rounded-lg p-4 text-sm space-y-2">
                <div className="flex justify-between">
                  <span>Distance:</span>
                  <span className="font-medium">{priceBreakdown.miles?.toFixed(1)} miles</span>
                </div>
                <div className="flex justify-between">
                  <span>Est. Duration:</span>
                  <span className="font-medium">{Math.round(priceBreakdown.hours * 60)} minutes</span>
                </div>
                {formData.stops.length > 0 && (
                  <div className="flex justify-between text-purple-600">
                    <span>Stop Fees ({formData.stops.length} stops):</span>
                    <span className="font-medium">+${(formData.stops.length * 15).toFixed(2)}</span>
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="earnings">Base Fare ($) *</Label>
                <Input
                  id="earnings"
                  name="earnings"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.earnings}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  required
                  data-testid="earnings-input"
                />
              </div>
              <div>
                <Label htmlFor="tips">Tips ($)</Label>
                <Input
                  id="tips"
                  name="tips"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.tips}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  data-testid="tips-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="notes">Notes (optional)</Label>
              <Textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Any special instructions for the driver..."
                rows={3}
                data-testid="notes-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Commission Breakdown */}
        {totalAmount > 0 && (
          <Card className="mt-4 border-green-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Commission Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between text-lg">
                  <span>Ride Total:</span>
                  <span className="font-bold">${totalAmount.toFixed(2)}</span>
                </div>
                <div className="border-t pt-3">
                  <div className="flex justify-between text-purple-600">
                    <span>Your Commission (20%):</span>
                    <span className="font-bold">${conciergeCommission.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600 mt-1">
                    <span>Driver Earnings (80%):</span>
                    <span>${driverEarnings.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="mt-6 flex gap-4">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={() => navigate('/concierge/dashboard')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="flex-1 bg-purple-600 hover:bg-purple-700"
            disabled={loading}
            data-testid="submit-ride-btn"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Check className="h-4 w-4 mr-2" />
            )}
            Post Ride
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ConciergeRideEntry;
